import React, { useState, useEffect, useRef } from 'react';
import {
    IconButton,
    Badge,
    Popover,
    List,
    ListItem,
    ListItemText,
    Typography,
    Box,
    Divider,
    CircularProgress,
    Snackbar,
    useMediaQuery,
    useTheme,
    LinearProgress,
    Paper,
    Avatar
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Delete as DeleteIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { subscribeToEvents } from '../utils/eventStream';

const NotificationBell = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [toastQueue, setToastQueue] = useState([]);
    const [currentToast, setCurrentToast] = useState(null);
    const [toastProgress, setToastProgress] = useState(100);
    const userInteracted = useRef(false);
    const initialLoadDone = useRef(false);

    useEffect(() => {
        // Listen for first user interaction
        const setInteracted = () => {
            userInteracted.current = true;
            window.removeEventListener('click', setInteracted);
            window.removeEventListener('keydown', setInteracted);
        };
        window.addEventListener('click', setInteracted);
        window.addEventListener('keydown', setInteracted);

        fetchUnreadCount();

        // Subscribe to real-time notification updates via the consolidated
        // SSE stream (one /api/events/stream connection shared app-wide).
        console.log('Subscribing to notification events...');

        let fallbackInterval = null;

        const unsubscribeUpdates = subscribeToEvents('notifications', () => {
            console.log('SSE Notifications: Received update - fetching unread count');
            fetchUnreadCount();
        });

        const unsubscribeErrors = subscribeToEvents('$error', () => {
            // EventSource automatically reconnects, but add fallback polling
            if (!fallbackInterval) {
                console.log('SSE Notifications: Starting fallback polling');
                fallbackInterval = setInterval(() => {
                    console.log('SSE Notifications: Fallback poll - fetching unread count');
                    fetchUnreadCount();
                }, 30000);
            }
        });

        return () => {
            console.log('SSE Notifications: Cleaning up subscription');
            unsubscribeUpdates();
            unsubscribeErrors();
            if (fallbackInterval) {
                clearInterval(fallbackInterval);
            }
            window.removeEventListener('click', setInteracted);
            window.removeEventListener('keydown', setInteracted);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Process toast queue - show next toast when current finishes
    useEffect(() => {
        if (!currentToast && toastQueue.length > 0) {
            console.log('🍞 Toast: Processing queue, showing next toast');
            const nextToast = toastQueue[0];
            setCurrentToast(nextToast);
            setToastQueue(prev => prev.slice(1));
            setToastProgress(100);
        }
    }, [toastQueue, currentToast]);

    // Setup timer and animation when a toast is showing
    useEffect(() => {
        if (!currentToast) return;

        console.log('🍞 Toast: Setting up progress animation and auto-dismiss');
        
        // Animate the progress bar from 100% to 0% over 5 seconds
        const progressInterval = setInterval(() => {
            setToastProgress(prev => {
                const newProgress = prev - 2; // Decrease by 2% every 100ms (100% / 50 steps = 2%)
                if (newProgress <= 0) {
                    return 0;
                }
                return newProgress;
            });
        }, 100);
        
        // Auto-dismiss after 5 seconds
        const dismissTimeout = setTimeout(() => {
            console.log('🍞 Toast: Auto-dismissing after 5 seconds');
            setCurrentToast(null);
            setToastProgress(100);
        }, 5000);

        // Cleanup when toast closes or component unmounts
        return () => {
            console.log('🍞 Toast: Cleaning up timers');
            clearTimeout(dismissTimeout);
            clearInterval(progressInterval);
        };
    }, [currentToast]);

    const handleToastClose = (event) => {
        if (event) {
            event.stopPropagation(); // Prevent triggering toast click when closing
        }
        console.log('🍞 Toast: Manually closing current toast');
        setCurrentToast(null);
        setToastProgress(100);
        // Cleanup will be handled automatically by the useEffect cleanup function
    };

    const handleToastClick = async () => {
        if (!currentToast) return;
        console.log('🍞 Toast: Clicked, navigating...');
        
        // Close toast immediately to prevent restart
        const notification = currentToast;
        setCurrentToast(null);
        setToastProgress(100);
        
        // Then navigate (don't close popover since we're not in popover context)
        await handleNotificationClick(notification, false);
    };

    const fetchUnreadCount = async () => {
        try {
            const response = await axios.get('/api/notifications/unread-count');
            const newCount = response.data.count;
            
            console.log(`🔔 Unread count: old=${unreadCount}, new=${newCount}, initialLoadDone=${initialLoadDone.current}, userInteracted=${userInteracted.current}`);

            // Check if we have new notifications
            if (initialLoadDone.current && newCount > unreadCount) {
                console.log('🔔 New notification detected!');
                
                // Play notification sound (only if user has interacted)
                if (userInteracted.current) {
                    const audio = new Audio('/sounds/notification.wav');
                    audio.play().catch(err => {
                        console.log('Audio playback prevented by browser autoplay policy');
                    });
                }

                // Update document title. `count` must be passed for i18next to
                // interpolate {{count}} (and pick the right plural form).
                document.title = `(${newCount}) ` + t('notifications.newNotifications', { count: newCount }) + ` - ` + t('title');

                // Fetch latest notification(s) to show in toast (desktop only, always show regardless of interaction)
                if (!isMobile) {
                    try {
                        const notifResponse = await axios.get('/api/notifications', { params: { limit: 1 } });
                        console.log('🍞 Toast: Fetched latest notification for toast:', notifResponse.data);
                        if (notifResponse.data && notifResponse.data.length > 0) {
                            const latestNotification = notifResponse.data[0];
                            // Add to toast queue
                            console.log('🍞 Toast: Adding to queue:', latestNotification);
                            setToastQueue(prev => {
                                const newQueue = [...prev, latestNotification];
                                console.log('🍞 Toast: Queue now has', newQueue.length, 'items');
                                return newQueue;
                            });
                        } else {
                            console.log('🍞 Toast: No notifications found to display');
                        }
                    } catch (err) {
                        console.error('Failed to fetch latest notification:', err);
                    }
                }
            }

            setUnreadCount(newCount);
            initialLoadDone.current = true;
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/notifications');
            setNotifications(response.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBellClick = async (event) => {
        setAnchorEl(event.currentTarget);
        await fetchNotifications();

        // Mark all as read when opening
        if (unreadCount > 0) {
            try {
                await axios.put('/api/notifications/read-all');
                setUnreadCount(0);
                // Update local notifications
                setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            } catch (error) {
                console.error('Error marking notifications as read:', error);
            }
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = async (notification, closePopover = true) => {
        try {
            // Mark as read if not already
            if (!notification.isRead) {
                await axios.put(`/api/notifications/${notification.id}/read`);
            }

            // Helper to get page for thread
            const getThreadPage = async (forumId, threadId, perPage = 5) => {
                const res = await axios.get(`/api/threads/forum/${forumId}`, { params: { limit: 10000, page: 1 } });
                const threads = res.data.threads;
                const idx = threads.findIndex(t => t.id === threadId || t.slug === threadId);
                return idx !== -1 ? Math.floor(idx / perPage) + 1 : 1;
            };

            // Handle forum/thread notifications
            if (notification.type === 'mention_thread' && notification.thread) {
                const forumSlug = notification.thread.forum?.slug || notification.thread.forum?.id;
                const threadSlug = notification.thread.slug || notification.thread.id;
                const forumId = notification.thread.forum?.id || notification.thread.forumId;
                const threadId = notification.thread.id || notification.thread.slug;
                const page = await getThreadPage(forumId, threadId, 5);
                navigate(`/dashboard/forums/${forumSlug}/threads/${threadSlug}?page=${page}`);
            } else if (notification.type === 'mention_post' && notification.post?.thread) {
                const forumSlug = notification.post.thread.forum?.slug || notification.post.thread.forum?.id;
                const threadSlug = notification.post.thread.slug || notification.post.thread.id;
                const forumId = notification.post.thread.forum?.id || notification.post.thread.forumId;
                const threadId = notification.post.thread.id || notification.post.thread.slug;
                const page = await getThreadPage(forumId, threadId, 5);
                navigate(`/dashboard/forums/${forumSlug}/threads/${threadSlug}?page=${page}`, {
                    state: { scrollToPost: notification.postId }
                });
            } else if ((notification.type === 'thread_activity' || notification.type === 'thread_reply') && notification.thread) {
                const forumSlug = notification.thread.forum?.slug || notification.thread.forum?.id;
                const threadSlug = notification.thread.slug || notification.thread.id;
                const forumId = notification.thread.forum?.id || notification.thread.forumId;
                const threadId = notification.thread.id || notification.thread.slug;
                const page = await getThreadPage(forumId, threadId, 5);
                navigate(`/dashboard/forums/${forumSlug}/threads/${threadSlug}?page=${page}`);
            }
            // Handle reminder notifications
            else if (['reminder_assignment', 'reminder_duplication', 'reminder_completion', 'task_completed', 'task_incomplete'].includes(notification.type) && notification.reminderId) {
                navigate(`/dashboard/reminders/${notification.reminderId}`);
            }
            // Handle public form submission notifications (admin)
            else if (notification.type === 'new_submission' && notification.submissionId) {
                navigate(`/dashboard/submissions?selected=${notification.submissionId}`);
            }

            // Only close the notification popover if requested (not from toast)
            if (closePopover) {
                handleClose();
            }
        } catch (error) {
            console.error('Error handling notification click:', error);
        }
    };

    const handleDismiss = async (notificationId, event) => {
        event.stopPropagation();
        try {
            await axios.delete(`/api/notifications/${notificationId}`);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } catch (error) {
            console.error('Error dismissing notification:', error);
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const created = new Date(date);
        const diffInSeconds = Math.floor((now - created) / 1000);

        if (diffInSeconds < 60) return t('notifications.justNow', 'Just now');
        if (diffInSeconds < 3600) return t('notifications.minutesAgo', { count: Math.floor(diffInSeconds / 60) });
        if (diffInSeconds < 86400) return t('notifications.hoursAgo', { count: Math.floor(diffInSeconds / 3600) });
        return t('notifications.daysAgo', { count: Math.floor(diffInSeconds / 86400) });
    };

    const getNotificationText = (notification) => {
        try {
            const data = JSON.parse(notification.content);
            const fromUserName = notification.fromUser
                ? `${notification.fromUser.firstName} ${notification.fromUser.lastName}`
                : 'Someone';

            if (data.key === 'mention_thread') {
                return t('notifications.mentionedInThread', {
                    name: fromUserName,
                    title: data.threadTitle
                });
            } else if (data.key === 'mention_post') {
                return t('notifications.mentionedInPost', {
                    name: fromUserName,
                    title: data.threadTitle
                });
            } else if (data.key === 'thread_activity') {
                return t('notifications.threadActivity', {
                    name: fromUserName,
                    title: data.threadTitle
                });
            } else if (data.key === 'thread_reply') {
                return t('notifications.threadReply', {
                    name: fromUserName,
                    threadTitle: data.threadTitle
                });
            } else if (data.key === 'task_completed') {
                return t('notifications.taskCompleted', {
                    changedBy: data.changedBy || fromUserName,
                    taskTitle: data.taskTitle,
                    reminderTitle: data.reminderTitle
                });
            } else if (data.key === 'task_incomplete') {
                return t('notifications.taskIncomplete', {
                    changedBy: data.changedBy || fromUserName,
                    taskTitle: data.taskTitle,
                    reminderTitle: data.reminderTitle
                });
            } else if (data.key === 'reminder_assignment') {
                return t('notifications.reminderAssignment', {
                    assignerName: data.assignerName || fromUserName,
                    reminderTitle: data.reminderTitle
                });
            } else if (data.key === 'reminder_duplication') {
                return t('notifications.reminderDuplication', {
                    reminderTitle: data.reminderTitle
                });
            } else if (data.key === 'reminder_completion') {
                return t('notifications.reminderCompletion', {
                    completedBy: data.completedBy || fromUserName,
                    reminderTitle: data.reminderTitle
                });
            } else if (data.key === 'new_submission') {
                return data.submissionType === 'job'
                    ? t('notifications.newSubmissionJob', { name: data.name })
                    : t('notifications.newSubmissionIdea', { name: data.name });
            }
        } catch (e) {
            // Fallback for old notification format
            return notification.content;
        }

        return notification.content;
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleBellClick}
                sx={{ ml: 1 }}
            >
                <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: { width: 400, maxHeight: 500 }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{t('notifications.title', 'Notifications')}</Typography>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : notifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('notifications.noNotifications', 'No notifications')}
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}>
                        {notifications.map((notification, index) => (
                            <React.Fragment key={notification.id}>
                                <ListItem
                                    button
                                    onClick={() => handleNotificationClick(notification)}
                                    sx={{
                                        backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                                        '&:hover': {
                                            backgroundColor: 'action.selected'
                                        }
                                    }}
                                >
                                    {/* Display picture of the user who tagged you */}
                                    {notification.fromUser && (
                                        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                                            <img
                                                src={notification.fromUser.displayPicture && notification.fromUser.displayPicture.startsWith('/') ? notification.fromUser.displayPicture : `https://static.vecteezy.com/system/resources/previews/052/755/981/non_2x/a-man-profile-avatar-icon-with-a-white-background-free-vector.jpg`}
                                                alt="Profile"
                                                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
                                            />
                                        </Box>
                                    )}

                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" sx={{ fontWeight: notification.isRead ? 400 : 600 }}>
                                                {getNotificationText(notification)}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="text.secondary">
                                                {formatTimeAgo(notification.createdAt)}
                                            </Typography>
                                        }
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleDismiss(notification.id, e)}
                                        sx={{ ml: 1 }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ListItem>
                                {index < notifications.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Popover>

            {/* Custom toast notification for new notifications (desktop only) */}
            {currentToast && !isMobile && (
                <Snackbar
                    open={!!currentToast}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    sx={{ 
                        '& .MuiSnackbarContent-root': { 
                            p: 0,
                            backgroundColor: 'transparent',
                            boxShadow: 'none'
                        } 
                    }}
                >
                    <Paper
                        elevation={8}
                        onClick={handleToastClick}
                        sx={{
                            width: 400,
                            maxWidth: '90vw',
                            backgroundColor: 'background.paper',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'scale(1.02)'
                            }
                        }}
                    >
                        {/* Progress bar */}
                        <LinearProgress
                            variant="determinate"
                            value={toastProgress}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: theme.palette.primary.main,
                                    transition: 'transform 0.1s linear'
                                }
                            }}
                        />
                        
                        <Box sx={{ p: 2, pt: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            {/* Profile picture */}
                            {currentToast.fromUser && (
                                <Avatar
                                    src={currentToast.fromUser.displayPicture && currentToast.fromUser.displayPicture.startsWith('/') 
                                        ? currentToast.fromUser.displayPicture 
                                        : 'https://static.vecteezy.com/system/resources/previews/052/755/981/non_2x/a-man-profile-avatar-icon-with-a-white-background-free-vector.jpg'}
                                    alt={`${currentToast.fromUser.firstName} ${currentToast.fromUser.lastName}`}
                                    sx={{ width: 40, height: 40 }}
                                />
                            )}
                            
                            {/* Notification content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {getNotificationText(currentToast)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatTimeAgo(currentToast.createdAt)}
                                </Typography>
                            </Box>

                            {/* Close button */}
                            <IconButton
                                size="small"
                                onClick={(e) => handleToastClose(e)}
                                sx={{ 
                                    ml: 0.5,
                                    mt: -0.5,
                                    color: 'text.secondary',
                                    '&:hover': { color: 'text.primary' }
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Paper>
                </Snackbar>
            )}
        </>
    );
};

export default NotificationBell;
